<template>
  <div>
    <b-row>
      <b-col cols="12">
        <cbs-card-actions
          ref="mainCard"
          :title="title"
          @refresh="refreshStop('mainCard')"
        >
          <b-card-body>

            <!-- search -->
            <div
              style="margin-bottom:10px; position: relative;"
              class="search-container"
            >
              <b-input-group class="input-group-merge">
                <b-input-group-prepend is-text>
                  <feather-icon icon="SearchIcon" />
                </b-input-group-prepend>
                <b-form-input
                  v-model="search"
                  placeholder="Search"
                  @keydown="handleSearchKeydown"
                  @focus="onSearchFocus"
                  @blur="onSearchBlur"
                />
              </b-input-group>
              <!-- History dropdown -->
              <div
                v-if="showHistory && filteredHistory.length > 0"
                class="search-history-dropdown"
              >
                <div
                  v-for="(item, index) in filteredHistory"
                  :key="index"
                  class="search-history-item"
                  :class="{ 'search-history-item-active': index === historyIndex }"
                  @mousedown.prevent="selectHistoryItem(item)"
                >
                  <feather-icon
                    icon="ClockIcon"
                    size="14"
                    class="mr-1"
                  />
                  {{ item }}
                </div>
              </div>
            </div>
            <!-- /search -->

            <!-- Table wrapper for keyboard events -->
            <div
              ref="tableWrapper"
              tabindex="0"
              style="outline: none;"
              @keydown="handleKeydown"
              @click="focusTable"
            >
              <!--<b-table responsive small
                       :fields="fields"
                       :items="objects"
                       :filter="search"
              >-->
              <b-table
                ref="objectsTable"
                responsive
                small
                :fields="fields"
                :items="filteredObjects()"
                selectable
                select-mode="single"
                :selected-rows="selectedRows"
                @row-selected="onRowSelected"
              >
                <template #cell(actions)="data">
                  <b-button-group
                    size="sm"
                    class="cbs-inline-spacing mb-1"
                  >
                    <b-button
                      size="sm"
                      variant="flat-primary"
                      target="_blank"
                      :href="'/cubus/object/' + data.item.id"
                      title="Open in new tab"
                    >
                      <feather-icon icon="ExternalLinkIcon" />
                    </b-button>
                    <b-button
                      size="sm"
                      variant="flat-primary"
                      :href="'/cubus/object/' + data.item.id"
                      title="Open object"
                    >
                      <feather-icon icon="LinkIcon" />
                    </b-button>
                    <b-button
                      size="sm"
                      variant="flat-primary"
                      title="Open object below"
                      @click="openObjectBelow(data.item.id)"
                    >
                      <feather-icon icon="CornerDownLeftIcon" />
                    </b-button>
                  </b-button-group>
                </template>
              </b-table>
            </div>
          </b-card-body>
        </cbs-card-actions>
      </b-col>
    </b-row>

    <!-- cards -->
    <div
      v-for="(card, index) in cards"
      :key="index"
    >
      <cbs-ctrl-object
        :objectid="card.objectid"
        @close="removeCard(index)"
      />
      <!--<cbs-object
        :objectid="card.objectid"
        @close="removeCard(index)"
      />-->
    </div>
    <!-- /cards -->
  </div>
</template>

<script>
import {
  BCardBody, BCol, BRow, BTable, BButton, BInputGroup, BInputGroupPrepend, BFormInput, BButtonGroup,
} from 'bootstrap-vue'
import useJwt from '@/cubus/jwt/useJwt'
import CbsCardActions from '@/cubus/components/cbs-card-actions/CbsCardActions.vue'
import useCubus from '@/cubus/services/useCubus'
import CbsCtrlObject from '@/cubus/components/object/CbsCtrlObject.vue'

export default {
  name: 'CbsObjectsTbl',
  components: {
    // CbsObject: () => import('@/cubus/components/object/CbsObject.vue'),
    BTable,
    BRow,
    BCol,
    BCardBody,
    BButton,
    CbsCardActions,
    BInputGroup,
    BInputGroupPrepend,
    BFormInput,
    CbsCtrlObject,
    BButtonGroup,
  },
  props: {
    metaFilter: {
      type: String,
      default: 'all',
    },
    title: {
      type: String,
      default: 'OBJECTS',
    },
  },
  data() {
    return {
      objects: [],
      fields: [
        'actions',
        {
          key: 'id',
          sortable: true,
        },
        {
          key: 'name',
          sortable: true,
        },
        {
          key: 'sid',
          sortable: true,
        },
        {
          key: 'meta_name',
          sortable: true,
        },
        {
          key: 'descr',
        },
      ],
      cardSet: new Set(),
      cardArr: [],
      cards: [],
      search: null,
      selectedRows: [],
      searchHistory: [],
      showHistory: false,
      historyIndex: -1,
      maxHistoryItems: 100,
      maxHistoryDisplay: 5,
    }
  },
  computed: {
    historyStorageKey() {
      return `cubus_objects_search_history_${this.metaFilter}`
    },
    filteredHistory() {
      if (!this.searchHistory || this.searchHistory.length === 0) return []
      const searchLower = (this.search || '').toLowerCase()
      let filtered
      if (!searchLower) {
        // Empty search - show most recent
        filtered = this.searchHistory.slice(0, this.maxHistoryDisplay)
      } else {
        // Filter history by current search term
        filtered = this.searchHistory
          .filter(item => item.toLowerCase().includes(searchLower))
          .slice(0, this.maxHistoryDisplay)
      }
      return filtered
    },
  },
  created() {
    this.loadHistory()
    this.loadObjects()
    setTimeout(() => {
      const searchContainer = document.querySelector('.search-container')
      const input = searchContainer.querySelector('input')
      input.focus()
    }, 100)
  },
  mounted() {
    // Focus the table wrapper so it can receive keyboard events
    this.$nextTick(() => {
      this.$refs.tableWrapper.focus({ preventScroll: true })
    })
  },
  methods: {
    loadObjects() {
      // console.log('load objects')
      useJwt.query({
        token: useJwt.getCubusToken(),
        query: {
          method: 'objects',
          param: { filter: this.metaFilter },
        },
      })
        .then(response => {
          console.log('objects response', response)
          if (response.data.error) {
            useCubus.toastError(this, response.data.error)
          } else if (response.data.objects) {
            this.objects = response.data.objects
          }
        })
        .catch(error => {
          console.log('objects error', error)
        })
    },
    openObjectBelow(objectid) {
      // this.cardSet.add(objectid)
      // this.initCardArr()
      this.addCard(objectid)
      this.$refs.mainCard.triggerCollapse()
    },
    addCard(objectid) {
      if (!this.isCardExists(objectid)) {
        this.cards.push({ objectid })
      }
    },
    isCardExists(objectid) {
      return this.cards.filter(card => card.objectid === objectid).length > 0
    },
    refreshStop(cardName) {
      this.loadObjects()
      setTimeout(() => {
        this.$refs[cardName].showLoading = false
      }, 1000)
    },
    closeCard(objectid) {
      this.cardSet.delete(objectid)
      // console.log('cardSet', this.cardSet)
      // this.cardArr = []
      // this.initCardArr()
    },
    initCardArr() {
      this.cardArr = []
      this.cardSet.forEach(el => {
        this.cardArr.push({ objectid: el })
      })
      // console.log('cardArr', this.cardArr)
    },
    removeCard(index) {
      console.log('index', index)
      console.log('cards before', this.cards)
      this.cards.splice(index, 1)
      console.log('cards after', this.cards)
    },
    filteredObjects() {
      if (!this.objects) return []
      if (!this.search || this.search === '') return this.objects
      return this.objects.filter(obj => this.filterBy(obj.name, this.search) || this.filterBy(obj.sid, this.search))
    },
    filterBy(title, search) {
      let pattern = search
        .replace(new RegExp(' ', 'gi'), '.*')
        .replace(new RegExp('\\.\\*\\.\\*', 'gi'), ' ')

      // Если исходная строка заканчивается пробелом
      if (search.endsWith(' ')) {
        // Если строка не начинается с пробела, добавляем .* в начало
        if (!search.startsWith(' ')) {
          pattern = `.*${pattern}`
        }
      }

      pattern = `^${pattern}.*`
      const re = new RegExp(pattern, 'gi')
      return re.test(title || '')
    },
    onRowSelected(items) {
      this.selectedRows = items
    },
    focusTable() {
      this.$refs.tableWrapper.focus({ preventScroll: true })
    },
    handleSearchKeydown(event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        event.preventDefault()

        // If history is visible, navigate history
        if (this.showHistory && this.filteredHistory.length > 0) {
          if (event.key === 'ArrowDown') {
            if (this.historyIndex < this.filteredHistory.length - 1) {
              this.historyIndex++
            }
          } else if (event.key === 'ArrowUp') {
            if (this.historyIndex > 0) {
              this.historyIndex--
            } else if (this.historyIndex === 0) {
              this.historyIndex = -1
            }
          }
          return
        }

        // Otherwise, focus the table and select first row if none selected
        this.focusTable()
        if (this.selectedRows.length === 0) {
          const filteredItems = this.filteredObjects()
          if (filteredItems.length > 0) {
            this.$refs.objectsTable.selectRow(0)
          }
        }
      } else if (event.key === 'Enter' && this.showHistory && this.historyIndex >= 0) {
        // Select history item on Enter
        event.preventDefault()
        this.selectHistoryItem(this.filteredHistory[this.historyIndex])
      } else if (event.key === 'Escape') {
        // Close history dropdown on Escape
        this.showHistory = false
        this.historyIndex = -1
      }
    },
    handleKeydown(event) {
      if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
        console.log('Arrow key pressed:', event.key)
        event.preventDefault()
        const filteredItems = this.filteredObjects()
        if (filteredItems.length === 0) return

        let currentIndex = -1
        if (this.selectedRows.length > 0) {
          currentIndex = filteredItems.findIndex(item => item.id === this.selectedRows[0].id)
          console.log('Current index:', currentIndex)
        }

        if (event.key === 'ArrowUp') {
          if (currentIndex > 0) {
            const newSelection = filteredItems[currentIndex - 1]
            this.$refs.objectsTable.clearSelected()
            this.$refs.objectsTable.selectRow(currentIndex - 1)
            console.log('Moving up to:', newSelection)
          }
        } else if (event.key === 'ArrowDown') {
          if (currentIndex < filteredItems.length - 1) {
            const newSelection = filteredItems[currentIndex + 1]
            this.$refs.objectsTable.clearSelected()
            this.$refs.objectsTable.selectRow(currentIndex + 1)
            console.log('Moving down to:', newSelection)
          } else if (currentIndex === -1) {
            const newSelection = filteredItems[0]
            this.$refs.objectsTable.clearSelected()
            this.$refs.objectsTable.selectRow(0)
            console.log('Selecting first item:', newSelection)
          }
        }
      } else if (event.key === 'Enter') {
        event.preventDefault()
        if (this.selectedRows.length > 0) {
          const selectedObject = this.selectedRows[0]
          const objectUrl = `/cubus/object/${selectedObject.id}`

          // Save search to history before navigating
          this.saveToHistory(this.search)

          if (event.shiftKey) {
            // Shift+Enter: Open in new tab

            const openInNewTab = href => {
              Object.assign(document.createElement('a'), {
                target: '_blank',
                rel: 'noopener noreferrer',
                href,
              }).click()
            }

            openInNewTab(objectUrl)
            console.log('Opening in new tab:', selectedObject)
          } else {
            // Enter: Open in current tab
            window.location.href = objectUrl
            console.log('Opening object:', selectedObject)
          }
        }
      }
    },
    // Search history methods
    loadHistory() {
      try {
        const stored = localStorage.getItem(this.historyStorageKey)
        if (stored) {
          this.searchHistory = JSON.parse(stored)
        }
      } catch (e) {
        console.warn('Failed to load search history:', e)
        this.searchHistory = []
      }
    },
    saveToHistory(term) {
      if (!term || term.trim() === '') return

      const trimmed = term.trim()

      // Remove if already exists (to move it to top)
      const index = this.searchHistory.indexOf(trimmed)
      if (index > -1) {
        this.searchHistory.splice(index, 1)
      }

      // Add to beginning
      this.searchHistory.unshift(trimmed)

      // Limit to max items
      if (this.searchHistory.length > this.maxHistoryItems) {
        this.searchHistory = this.searchHistory.slice(0, this.maxHistoryItems)
      }

      // Save to localStorage
      try {
        localStorage.setItem(this.historyStorageKey, JSON.stringify(this.searchHistory))
      } catch (e) {
        console.warn('Failed to save search history:', e)
      }
    },
    selectHistoryItem(item) {
      this.search = item
      this.showHistory = false
      this.historyIndex = -1
    },
    onSearchFocus() {
      this.showHistory = true
      this.historyIndex = -1
    },
    onSearchBlur() {
      // Save search to history when leaving the input
      this.saveToHistory(this.search)

      // Delay to allow click on history item
      setTimeout(() => {
        this.showHistory = false
        this.historyIndex = -1
      }, 150)
    },
  },
}
</script>

<style scoped>
/* Style the table wrapper when focused */
div[tabindex]:focus {
  outline: 2px solid #007bff;
  outline-offset: 2px;
  border-radius: 4px;
}

/* Search history dropdown */
.search-history-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #d8d6de;
  border-radius: 0.357rem;
  box-shadow: 0 4px 24px 0 rgba(34, 41, 47, 0.1);
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
}

.search-history-item {
  padding: 0.5rem 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  color: #6e6b7b;
}

.search-history-item:hover,
.search-history-item-active {
  background-color: #f8f8f8;
  color: #7367f0;
}

/* Dark mode support */
.dark-layout .search-history-dropdown {
  background: #283046;
  border-color: #3b4253;
}

.dark-layout .search-history-item {
  color: #b4b7bd;
}

.dark-layout .search-history-item:hover,
.dark-layout .search-history-item-active {
  background-color: #161d31;
  color: #7367f0;
}
</style>
